/**
 * The report table (SPEC §9.4).
 *
 * Sticky header, sortable, column show/hide, a totals row from
 * `ColumnSpec.total`, and virtualization above 500 rows.
 *
 * **Rows are positional arrays** (§8.6). The renderer indexes `columns`; there
 * are no keys on a row, and adding them "for convenience" would double the
 * payload of every wide result set. Everything numeric renders through
 * `lib/format.ts` with the `.numeric` class — that is a hard rule, and it is
 * why a column of figures lines up under itself.
 */

import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, Columns3 } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { asNumber, columnAlign, columnTotal, formatCell, formatNumber } from '../lib/format'
import type { Cell, ColumnSpec } from '../lib/types'

/** Above this many rows the body is windowed rather than fully mounted (§9.4). */
export const VIRTUALIZE_ABOVE = 500

const ROW_HEIGHT = 33

export interface DataTableProps {
  readonly columns: readonly ColumnSpec[]
  readonly rows: readonly (readonly Cell[])[]
  readonly caption: string
}

interface SortState {
  readonly index: number
  readonly direction: 'asc' | 'desc'
}

const ALIGN_CLASS = { left: 'text-left', right: 'text-right', center: 'text-center' } as const

/**
 * Compare two cells of one column.
 *
 * Numeric columns compare as numbers, not as text — otherwise 10 sorts before
 * 9 and the table is worse than useless. Nulls always sort last regardless of
 * direction: a missing value is not the smallest value.
 */
function compareCells(left: Cell, right: Cell, column: ColumnSpec): number {
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1

  if (column.kind !== 'string' && column.kind !== 'date' && column.kind !== 'datetime') {
    const a = asNumber(left)
    const b = asNumber(right)
    if (a !== null && b !== null) return a - b
  }
  return String(left).localeCompare(String(right))
}

export function DataTable({ columns, rows, caption }: DataTableProps) {
  // `hidden` columns are available to charts but not shown here (§8.2).
  const shown = useMemo(
    () =>
      columns
        .map((column, index) => ({ column, index }))
        .filter((entry) => !entry.column.hidden),
    [columns],
  )
  const [dropped, setDropped] = useState<ReadonlySet<string>>(new Set())
  const [sort, setSort] = useState<SortState | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const visible = shown.filter((entry) => !dropped.has(entry.column.key))

  const sorted = useMemo(() => {
    if (sort === null) return rows
    const column = columns[sort.index]
    const factor = sort.direction === 'asc' ? 1 : -1
    // Copied before sorting: the array belongs to the query cache.
    if (column === undefined) return rows
    return [...rows].sort(
      (left, right) =>
        factor * compareCells(left[sort.index] ?? null, right[sort.index] ?? null, column),
    )
  }, [rows, sort, columns])

  const totals = useMemo(
    () =>
      visible.map((entry) =>
        columnTotal(
          entry.column,
          rows.map((row) => row[entry.index] ?? null),
        ),
      ),
    [visible, rows],
  )
  const hasTotals = totals.some((total) => total !== null)

  const virtualize = sorted.length > VIRTUALIZE_ABOVE
  const virtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    enabled: virtualize,
  })
  const virtualRows = virtualizer.getVirtualItems()

  const toggleSort = (index: number): void => {
    setSort((current) =>
      current === null || current.index !== index
        ? { index, direction: 'asc' }
        : current.direction === 'asc'
          ? { index, direction: 'desc' }
          : null,
    )
  }

  const headerCells = visible.map((entry) => {
    const align = columnAlign(entry.column)
    const active = sort?.index === entry.index
    return (
      <th
        key={entry.column.key}
        scope="col"
        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`sticky top-0 z-10 border-b border-[var(--color-rule)] bg-[var(--color-surface-raised)] px-3 py-2 font-medium ${ALIGN_CLASS[align]}`}
      >
        <button
          type="button"
          onClick={() => {
            toggleSort(entry.index)
          }}
          className={`inline-flex items-center gap-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] ${
            align === 'right' ? 'flex-row-reverse' : ''
          }`}
        >
          {entry.column.label}
          {active &&
            (sort.direction === 'asc' ? (
              <ArrowUp aria-hidden className="size-3" />
            ) : (
              <ArrowDown aria-hidden className="size-3" />
            ))}
        </button>
      </th>
    )
  })

  const renderRow = (row: readonly Cell[], key: number) => (
    <tr key={key} className="border-b border-[var(--color-rule)]/60">
      {visible.map((entry) => {
        const value = row[entry.index] ?? null
        const align = columnAlign(entry.column)
        const numeric = align === 'right' || entry.column.kind !== 'string'
        return (
          <td
            key={entry.column.key}
            className={`px-3 py-1.5 ${ALIGN_CLASS[align]} ${numeric ? 'numeric' : ''}`}
          >
            {formatCell(entry.column, value)}
          </td>
        )
      })}
    </tr>
  )

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] elevated">
      <div className="flex items-center justify-between border-b border-[var(--color-rule)] px-3 py-2">
        <p className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          <span className="numeric">{formatNumber(rows.length)}</span>{' '}
          {rows.length === 1 ? 'row' : 'rows'}
        </p>
        <div className="relative">
          <button
            type="button"
            aria-expanded={pickerOpen}
            onClick={() => {
              setPickerOpen((open) => !open)
            }}
            className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-rule)] px-2 py-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]"
          >
            <Columns3 aria-hidden className="size-3.5" />
            Columns
          </button>
          {pickerOpen && (
            <div className="absolute right-0 z-20 mt-1 w-52 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-2 elevated">
              {shown.map((entry) => (
                <label
                  key={entry.column.key}
                  className="flex items-center gap-2 px-1 py-1 text-[length:var(--text-xs)]"
                >
                  <input
                    type="checkbox"
                    checked={!dropped.has(entry.column.key)}
                    onChange={() => {
                      setDropped((current) => {
                        const next = new Set(current)
                        if (next.has(entry.column.key)) next.delete(entry.column.key)
                        else next.add(entry.column.key)
                        return next
                      })
                    }}
                  />
                  {entry.column.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="max-h-[32rem] overflow-auto">
        <table className="w-full border-collapse text-[length:var(--text-sm)]">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>{headerCells}</tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={visible.length}
                  className="px-3 py-8 text-center text-[var(--color-ink-muted)]"
                >
                  No rows for these parameters.
                </td>
              </tr>
            )}

            {virtualize ? (
              <>
                {/* Spacers stand in for the rows above and below the window,
                    so the scrollbar reflects the whole result set. */}
                <tr style={{ height: virtualRows[0]?.start ?? 0 }} aria-hidden />
                {virtualRows.map((virtualRow) => {
                  const row = sorted[virtualRow.index]
                  return row === undefined ? null : renderRow(row, virtualRow.index)
                })}
                <tr
                  style={{
                    height:
                      virtualizer.getTotalSize() -
                      (virtualRows[virtualRows.length - 1]?.end ?? virtualizer.getTotalSize()),
                  }}
                  aria-hidden
                />
              </>
            ) : (
              sorted.map(renderRow)
            )}
          </tbody>

          {hasTotals && (
            <tfoot>
              <tr className="sticky bottom-0 border-t border-[var(--color-rule)] bg-[var(--color-surface-raised)] font-medium">
                {visible.map((entry, position) => {
                  const total = totals[position] ?? null
                  const align = columnAlign(entry.column)
                  return (
                    <td
                      key={entry.column.key}
                      className={`px-3 py-2 ${ALIGN_CLASS[align]} ${total === null ? '' : 'numeric'}`}
                    >
                      {total === null
                        ? position === 0
                          ? 'Total'
                          : ''
                        : formatCell(entry.column, total)}
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
